// The pure side of the video noise reducer: measuring how noisy a picture is, what the looks are, and what each setting means. No drawing here.

// How much each part of the cleaning does (all 0..1):
//   temporal - averages with the frame before where nothing moves (the strongest way to remove video noise);
//   spatial  - smooths grain inside a frame without blurring edges;
//   chroma   - removes colour speckles;
//   detail   - brings back fine edges and texture that the smoothing took away;
//   deblock  - smooths the square blocks of heavily compressed video.
export const LOOKS = {
  light: { label: "Light", about: "Slight grain, a good camera in good light.", temporal: 0.45, spatial: 0.25, chroma: 0.4, detail: 0.6, deblock: 0 },
  medium: { label: "Medium", about: "Visible grain, for example a phone video indoors.", temporal: 0.65, spatial: 0.45, chroma: 0.6, detail: 0.55, deblock: 0.15 },
  strong: { label: "Strong", about: "Heavy grain, a dim room or an old camera.", temporal: 0.8, spatial: 0.7, chroma: 0.8, detail: 0.45, deblock: 0.3 },
  night: { label: "Night / very noisy", about: "Very noisy footage: smooths hard and keeps the main edges.", temporal: 0.9, spatial: 0.85, chroma: 0.9, detail: 0.35, deblock: 0.4 },
  blocks: { label: "Compressed video", about: "Blocky or smeared web video: removes the squares, little else.", temporal: 0.35, spatial: 0.3, chroma: 0.5, detail: 0.6, deblock: 0.85 },
  custom: { label: "Custom", about: "Set every part yourself.", temporal: 0.6, spatial: 0.4, chroma: 0.5, detail: 0.5, deblock: 0.2 },
};
export const LOOK_IDS = Object.keys(LOOKS);
export const PARTS = [
  ["temporal", "Frame averaging", "Averages still parts of the picture over time. Strongest effect; moving things are left alone."],
  ["spatial", "Grain smoothing", "Smooths grain inside each frame while keeping edges."],
  ["chroma", "Colour noise", "Removes coloured speckles, common in low light."],
  ["detail", "Keep detail", "Higher brings back more fine edges and texture."],
  ["deblock", "Compression blocks", "Smooths the squares that heavy compression leaves."],
];

const clamp01 = (x) => Math.min(1, Math.max(0, Number.isFinite(x) ? x : 0));

// The settings of a look; the sliders' values when the look is "custom".
export function settingsOf(look, custom = {}) {
  const base = LOOKS[look] || LOOKS.medium;
  const from = look === "custom" ? { ...base, ...custom } : base;
  return Object.fromEntries(PARTS.map(([key]) => [key, clamp01(from[key])]));
}

// ------------------------------------------------------------------------------------------ measuring noise
// The brightness of every pixel (0..255) from the RGBA bytes of a picture.
export function lumaOf(rgba, count = rgba.length / 4) {
  const out = new Float32Array(count);
  for (let i = 0; i < count; i += 1) out[i] = 0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2];
  return out;
}

// The noise of a picture as a standard deviation in brightness levels (0..255), by a method like Immerkaer's: a filter that cancels smooth areas and edges and leaves
// the noise, measured in small blocks of which the quieter ones are taken (a block full of texture would look noisy). 0 for a clean picture, about 2-4 for a
// light grain, 8 or more for heavy grain.
export function estimateSigma(luma, w, h, block = 24) {
  const sigmas = [];
  for (let by = 0; by + block <= h; by += block) {
    for (let bx = 0; bx + block <= w; bx += block) {
      const responses = [];
      for (let y = by + 1; y < by + block - 1; y += 1) {
        for (let x = bx + 1; x < bx + block - 1; x += 1) {
          const k = y * w + x;
          const v = luma[k - w - 1] - 2 * luma[k - w] + luma[k - w + 1] - 2 * luma[k - 1] + 4 * luma[k] - 2 * luma[k + 1] + luma[k + w - 1] - 2 * luma[k + w] + luma[k + w + 1];
          responses.push(Math.abs(v));
        }
      }
      responses.sort((a, b) => a - b);
      const n = responses.length;
      // the filter turns noise of deviation s into noise of deviation 6s, whose middle absolute value is 0.6745 of that: so s = middle / 4.047. (The middle, not the average:
      // an edge changes only a few responses and the middle does not notice them.)
      if (n) sigmas.push(responses[Math.floor(n / 2)] / 4.047);
    }
  }
  if (!sigmas.length) return 0;
  sigmas.sort((a, b) => a - b);
  return sigmas[Math.floor((sigmas.length - 1) * 0.3)];
}

// The middle one of several measurements (of different frames).
export function medianOf(values) {
  const list = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!list.length) return 0;
  const mid = Math.floor(list.length / 2);
  return list.length % 2 ? list[mid] : (list[mid - 1] + list[mid]) / 2;
}

export const LEVELS = [
  { max: 1.8, id: "clean", label: "Very clean", look: "light" },
  { max: 3.5, id: "light", label: "Light noise", look: "light" },
  { max: 7, id: "moderate", label: "Moderate noise", look: "medium" },
  { max: 12, id: "heavy", label: "Heavy noise", look: "strong" },
  { max: Infinity, id: "severe", label: "Very heavy noise", look: "night" },
];
export const levelFor = (sigma) => LEVELS.find((l) => sigma < l.max) || LEVELS[LEVELS.length - 1];

// The noise level the filters work from (0..1 of the brightness range), kept within what is sensible.
export const sigmaUnit = (sigma, fallback = 0.03) => (Number.isFinite(sigma) && sigma > 0 ? Math.min(0.12, Math.max(0.008, sigma / 255)) : fallback);

// What the filters work from when the noise was not measured: how much the look expects.
export const sigmaOfLook = (look) => ({ light: 0.015, medium: 0.03, strong: 0.05, night: 0.075, blocks: 0.02, custom: 0.03 }[look] || 0.03);
